/**
 * Merges a player's single-player scout reports with their evaluations inside
 * match reports into the one list the player page renders (SE-7856).
 *
 * ## One row per MATCH, not per evaluation
 *
 * `Player.matchReportEvaluations` returns one row per evaluation, but two
 * scouts watching the same fixture produce two evaluations of the same player
 * — `createMatchReport` is idempotent per (match, scout), so the multi-scout
 * case arrives as separate reports on one match. Rendering those as separate
 * rows would tell a reader the player was watched twice as often as they were,
 * which is the rule `toPlayerReportRows` already enforces for the aggregation
 * section directly above this list.
 *
 * So evaluations are grouped by `matchId`: the first scout to evaluate is the
 * row's OWNER, and every later read hangs off it as `others`. Figma frame 26
 * draws exactly this — "Written report ownership: first scout to evaluate",
 * with the second scout's numbers inside the expanded body.
 *
 * ## Why a tagged row rather than a bare union
 *
 * Both fragments carry `reportKind`, but codegen types it as the whole
 * `ReportKind` enum on both sides rather than as the literal each one always
 * is, so it does NOT narrow a union in TypeScript. `__typename` does narrow,
 * but it is optional in the generated types. Rather than lean on either, the
 * tag is CONSTRUCTED here, so every consumer gets a real discriminant and a
 * compiler error if it forgets a kind.
 *
 * ## The sort key is the date each row DISPLAYS
 *
 * A match row shows its fixture date; a scout row shows when the report was
 * written. Sorting on a single field would therefore order the list by
 * something the screen does not show — and on dev these differ by a month on
 * real rows, so the visible dates would not descend. Each row instead sorts on
 * the value it renders.
 *
 * That means comparing a civil `YYYY-MM-DD` against an ISO instant, which must
 * never be done with `localeCompare`: a civil date is a string PREFIX of an
 * instant on the same day, so a report filed on the day of its match sorts
 * purely by string shape. `toDateKey` reduces both to a UTC calendar day,
 * which is the comparison this list actually means. It is reused rather than
 * re-derived precisely because `useUnifiedReports` already shipped that bug
 * once.
 * @module features/reports/utils/mergePlayerReportRows
 */

import { toDateKey } from "@/features/scouting/utils/reportDate";
import {
  PlayerDetailMatchReportEvaluationFragment,
  PlayerDetailScoutReportsFragment,
  ReportKind,
} from "@/generated/graphql";

/** One row of the player page's merged reports list, tagged by its origin. */
export type PlayerReportRow =
  | {
      readonly kind: ReportKind.Scout;
      /** Stable identity for `keyExtractor`. */
      readonly rowId: string;
      /**
       * The server's resolved ordering day, `YYYY-MM-DD` (SE-7935).
       *
       * Non-null by schema, and NEVER rendered — `displayDate` answers what
       * the row shows, which is a different question. For a scout report
       * covering a fixture this is that fixture's day; for one covering none
       * it is the report's creation day, which is the main path rather than an
       * edge case (34 of 92 scout reports on dev cover no fixture).
       */
      readonly sortDate: string;
      /**
       * The instant this row breaks ties on, when two rows share a `sortDate`.
       *
       * `sortDate` resolves to a DAY, so same-day rows compare equal on it.
       * Carried explicitly because the tie-break is mandatory, not defensive —
       * see `byFixtureDateDescending`.
       */
      readonly tieBreak: string | null;
      /**
       * The raw date this row renders. A scout report has one date, so this is
       * the same value the sort key came from — carried anyway so both kinds
       * answer "what date does this row show?" the same way.
       */
      readonly displayDate: string | null;
      readonly report: PlayerDetailScoutReportsFragment;
    }
  | {
      readonly kind: ReportKind.Match;
      /**
       * The match id, not a report or evaluation id. The row IS the fixture,
       * and inline expansion is keyed by it.
       */
      readonly rowId: string;
      /** The server's resolved ordering day — see the scout row above. */
      readonly sortDate: string;
      /** The instant this row breaks ties on — see the scout row above. */
      readonly tieBreak: string | null;
      /**
       * The raw date this row RENDERS, which is NOT what it sorts on
       * (SE-7935).
       *
       * Exposed rather than re-derived at the render site: computing
       * `matchDate ?? reportDate` there looks equivalent and is not, because
       * `??` only rejects nullish while the sort rejects anything `toDateKey`
       * cannot parse. A fixture dated `2026-02-30` took the report date for
       * ordering and printed an em dash.
       */
      readonly displayDate: string | null;
      readonly matchId: string;
      /** The first scout to evaluate this player in this fixture. */
      readonly owner: PlayerDetailMatchReportEvaluationFragment;
      /** Every later read of the same player in the same fixture. */
      readonly others: readonly PlayerDetailMatchReportEvaluationFragment[];
    };

/**
 * Orders evaluations within one fixture so the earliest read comes first.
 *
 * Ownership follows the work: whoever attended to this player first owns the
 * row. `notedAt` is when that happened. A row that was never noted cannot
 * claim ownership over one that was, so it sinks.
 *
 * The order is TOTAL, not merely correct: see the `evaluationId` fallback.
 * @param left - The first evaluation.
 * @param right - The second evaluation.
 * @returns Negative when `left` was noted first.
 */
const byNotedAtAscending = (
  left: PlayerDetailMatchReportEvaluationFragment,
  right: PlayerDetailMatchReportEvaluationFragment
): number => {
  if (left.notedAt !== right.notedAt) {
    if (!left.notedAt) return 1;
    if (!right.notedAt) return -1;

    return left.notedAt < right.notedAt ? -1 : 1;
  }

  // Equal or both-absent `notedAt` used to return 0, which left the OWNER
  // decided by whatever order the server happened to send (CodeRabbit,
  // SE-7935). That was invisible until this ticket, because ownership only
  // affected which scout's name led the row. It is observable now: the row
  // takes its tie-break instant from `owner.reportDate`, so a different owner
  // can reorder same-day rows — which would defeat the determinism this change
  // exists to provide.
  //
  // `evaluationId` is the stable key. It is never displayed and never compared
  // across rows; it exists only to make this choice repeatable.
  if (left.evaluationId === right.evaluationId) return 0;

  return left.evaluationId < right.evaluationId ? -1 : 1;
};

/**
 * The date a match row DISPLAYS. It no longer decides where the row sorts.
 *
 * Until SE-7935 this returned the sort key and the display date as a pair, so
 * the row could not sort on one date and print another. The server resolves
 * the ordering now (`sortDate`), and this keeps only the display half — which
 * still needs the same validation, for the same reason: `matchDate` is
 * nullable AND can be present-but-unparseable, and a row must print an em dash
 * rather than a broken date.
 *
 * `toDateKey` is used to VALIDATE, not to order: it answers "is this a date at
 * all", and the raw value is what gets rendered. That is why the fallback to
 * `reportDate` survives — a fixture whose date precision left it unset still
 * shows something true about the row.
 * @param evaluation - The owning evaluation of the row.
 * @returns The raw date to render, or null when neither date is usable.
 */
const matchRowDisplayDate = (
  evaluation: PlayerDetailMatchReportEvaluationFragment
): string | null => {
  if (toDateKey(evaluation.matchDate ?? null) !== null) {
    return evaluation.matchDate ?? null;
  }

  if (toDateKey(evaluation.reportDate ?? null) !== null) {
    return evaluation.reportDate ?? null;
  }

  return null;
};

/**
 * Orders rows newest-first by the fixture day the SERVER resolved (SE-7935).
 *
 * Not by the date each row displays. A report written weeks after the match it
 * covers now sits beside its fixture rather than at the top of the list, which
 * is the whole point: the calendar surface shows the fixture date, and a scout
 * moving between the two screens should read one chronology, not two.
 *
 * THE TIE-BREAK IS MANDATORY, NOT DEFENSIVE. `sortDate` resolves to a DAY, so
 * every pair of rows from the same day compares equal on it. Without a further
 * key the comparator is not *wrong* — it is NON-DETERMINISTIC between renders,
 * which is far harder to diagnose than a wrong order. `Array.prototype.sort`
 * stability does not rescue this: the two sources are concatenated and then
 * re-sorted, so the input order is itself a product of the merge.
 *
 * `reportDate` is the tie-break because it is a real instant and is present on
 * both kinds. The row id settles the remainder, so the order is total.
 *
 * No date arithmetic anywhere here, deliberately. `sortDate` is a civil
 * `YYYY-MM-DD` string and is compared as one. Parsing it into a `Date` would
 * read it as midnight UTC and shift the day for viewers east of UTC — the
 * mirror of the bug SE-7928 hit on the backend, and invisible in CI, which
 * runs UTC where both directions of that error look correct.
 * @param left - The first row.
 * @param right - The second row.
 * @returns Negative when `left` sorts first, positive when `right` does.
 */
const byFixtureDateDescending = (
  left: PlayerReportRow,
  right: PlayerReportRow
): number => {
  // Lexical comparison is safe ONLY because both sides are civil `YYYY-MM-DD`
  // days resolved by the server — never a mix of civil dates and instants.
  if (left.sortDate !== right.sortDate) {
    return left.sortDate < right.sortDate ? 1 : -1;
  }

  // Same day. `reportDate` is nullable on both kinds, so a row without one
  // sinks WITHIN the day rather than sorting as the epoch.
  if (left.tieBreak !== right.tieBreak) {
    if (left.tieBreak === null) return 1;
    if (right.tieBreak === null) return -1;

    return left.tieBreak < right.tieBreak ? 1 : -1;
  }

  // Same day and same instant: the id is what makes the order TOTAL, and a
  // total order is what makes it repeatable between renders.
  if (left.rowId === right.rowId) return 0;

  return left.rowId < right.rowId ? -1 : 1;
};

/**
 * Groups a player's match-report evaluations into one row per fixture.
 * @param matchReportEvaluations - Evaluations already projected to this player.
 * @returns One row per `matchId`, each carrying its owner and later reads.
 */
const toMatchRows = (
  matchReportEvaluations: readonly PlayerDetailMatchReportEvaluationFragment[]
): PlayerReportRow[] => {
  const byMatch = matchReportEvaluations.reduce<
    ReadonlyMap<string, readonly PlayerDetailMatchReportEvaluationFragment[]>
  >((accumulator, evaluation) => {
    const existing = accumulator.get(evaluation.matchId) ?? [];

    return new Map([
      ...accumulator,
      [evaluation.matchId, [...existing, evaluation]],
    ]);
  }, new Map());

  return [...byMatch.entries()].map(([matchId, evaluations]) => {
    const [owner, ...others] = [...evaluations].sort(byNotedAtAscending);
    // Display only. The server resolves the ordering, but `matchDate` can
    // still be absent or unparseable and the row must print an em dash rather
    // than a broken date.
    const displayDate = matchRowDisplayDate(owner);

    return {
      kind: ReportKind.Match,
      rowId: matchId,
      sortDate: owner.sortDate,
      tieBreak: owner.reportDate ?? null,
      displayDate,
      matchId,
      owner,
      others,
    } as const;
  });
};

/**
 * Merges the two report sources into one newest-first list.
 * @param scoutReports - Single-player scout reports for the player.
 * @param matchReportEvaluations - The player's own evaluations inside match
 * reports, already projected to this player by the server.
 * @returns One list, newest-first by the date each row displays.
 */
export const mergePlayerReportRows = (
  scoutReports: readonly PlayerDetailScoutReportsFragment[] | null | undefined,
  matchReportEvaluations:
    | readonly PlayerDetailMatchReportEvaluationFragment[]
    | null
    | undefined
): readonly PlayerReportRow[] => {
  const scoutRows: PlayerReportRow[] = (scoutReports ?? []).map(
    (report, index) => ({
      kind: ReportKind.Scout,
      // `reportId` is nullable on PlayerScoutReport, so fall back to the index
      // rather than emitting a duplicate empty key across several rows.
      rowId: report.reportId ?? `scout-${index}`,
      sortDate: report.sortDate,
      tieBreak: report.reportDate ?? null,
      displayDate: report.reportDate ?? null,
      report,
    })
  );

  // Array.prototype.sort is stable, so rows sharing a calendar day keep the
  // order the server sent them in rather than swapping between renders.
  return [...scoutRows, ...toMatchRows(matchReportEvaluations ?? [])].sort(
    byFixtureDateDescending
  );
};
